import React, { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Moon, Sun, Globe, User, LogOut, BookOpen } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { CreditDisplay } from './credits/CreditDisplay';
import { Logo } from './icons/Logo';

// 懒加载 LoginModal
const LoginModal = lazy(() => import('./auth/LoginModal').then(module => ({ default: module.LoginModal })));

interface HeaderProps {
  darkMode: boolean;
  toggleDarkMode: () => void;
}

export const Header: React.FC<HeaderProps> = ({ darkMode, toggleDarkMode }) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const { user, signOut, isAuthenticated } = useAuth();
  const [showLogin, setShowLogin] = React.useState(false);
  const [showUserMenu, setShowUserMenu] = React.useState(false);

  const toggleLanguage = () => {
    const cur = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase();
    const newLang = cur.startsWith('zh') ? 'en' : 'zh';
    i18n.changeLanguage(newLang);
  };

  const handleSignOut = async () => {
    await signOut();
    setShowUserMenu(false);
  };
  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8">
              <Logo className="w-full h-full text-purple-500" />
            </div>
            <span className="font-semibold text-gray-900 dark:text-white">{t('brand.mark')}</span>
          </div>
          
          <div className="flex items-center space-x-4">
            {isAuthenticated && user && (
              <div className="hidden md:block">
                <CreditDisplay showActions={false} />
              </div>
            )}
            
            <button
              type="button"
              onClick={toggleLanguage}
              className="flex items-center space-x-1 px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              title={`Switch to ${i18n.language === 'en' ? '中文' : 'English'}`}
            >
              <Globe className="w-4 h-4" />
              <span className="text-sm font-medium">{t('common.language')}</span>
            </button>
            
            <button
              type="button"
              onClick={toggleDarkMode}
              className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            
            {isAuthenticated && user ? (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  <User className="w-5 h-5" />
                  <span className="hidden sm:inline text-sm font-medium">
                    {user.name || user.email}
                  </span>
                </button>
                
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2">
                    <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {user.name || user.email}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {user.email}
                      </p>
                    </div>
                    <div className="md:hidden px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                      <CreditDisplay showActions={false} />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        navigate('/my-readings');
                        setShowUserMenu(false);
                      }}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <BookOpen className="w-4 h-4" />
                      <span>我的查询</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleSignOut}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>{t('common.signOut')}</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowLogin(true)}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-full font-medium hover:shadow-lg transition-all duration-300"
              >
                {t('common.login')}
              </button>
            )}
          </div>
        </div>
      </div>
      </header>

      {/* 懒加载 LoginModal - 只在需要时加载 */}
      {showLogin && (
        <Suspense fallback={null}>
          <LoginModal
            isOpen={showLogin}
            onClose={() => setShowLogin(false)}
          />
        </Suspense>
      )}
    </>
  );
};